/** React libraries */
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppRouter } from './routes/AppRouter';
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { ToastContainer } from 'react-toastify';

/** Components, Utitlity, Types, Constants */
import { reactPlugin } from './utils/appInsights';
import { DEFAULT_ERROR_MSG, DEFAULT_LANGUAGE, WEGLOT_KEY } from './utils/constants';
import { LoaderProvider } from "./context/LoaderContext";
import { BannerData, bannerService } from "./services/bannerAPI";
import Banner from "./components/Banner/Banner";

/** Style */
import './App.css';
import "../src/assets/css/colors.css";
import "../src/assets/css/styles.css";
import "../src/assets/js/main.js"
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../src/assets/css/style.min.css';
import '../src/assets/css/fonts.css';

// Custom hook to trigger Weglot on route change
const useWeglotOnRouteChange = () => {
  const location = useLocation();

  useEffect(() => {
    // Give React time to render new page before refreshing translation
    const timer = setTimeout(() => {
      if (window.Weglot) {
        const current = window.Weglot.getCurrentLang();
        window.Weglot.switchTo(current);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);
};


function App() {

  const [lang, setLang] = useState<string | null>(null);
  const [isWeglotReady, setIsWeglotReady] = useState(false);
  const [bannerListData, setBannerListData] = useState<BannerData[]>([]);
  const [bannerVisited, setBannerVisited] = useState(
    localStorage.getItem("bannerVisited") === "true"
  );

  useWeglotOnRouteChange();

  const getLanguage = (): string => {
    try {
      const storedLanguage = localStorage.getItem("selectedLang");
      return storedLanguage || DEFAULT_LANGUAGE;
    } catch (error) {
      return DEFAULT_LANGUAGE;
    }
  };

  /**
  *  Fetch banner
  */
  const fetchBanner = async () => {
    try {
      let result = await bannerService.fetchBanners();
      if (result && result[0]?.id) {
        setBannerListData(prev => [...prev, ...result]);
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchBanner();
  }, []);


  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.weglot.com/weglot.min.js";
    script.async = true;

    script.onload = () => {
      if (window.Weglot) {
        // Add delay before initializing
        const delay = 10;

        const timer = setTimeout(() => {
          window.Weglot.initialize({
            api_key: WEGLOT_KEY,
            originalLanguage: getLanguage(),
            destinationLanguages: ["es", "nl", "sv"],
            excludedBlocks: [".no-translate"]
          });

          window.Weglot.on("initialized", () => {
            setIsWeglotReady(true);

            const storedLanguage = getLanguage();
            if (storedLanguage && storedLanguage !== DEFAULT_LANGUAGE) {
              window.Weglot.switchTo(storedLanguage);
            }
          });

          window.Weglot.on("languageChanged", (newLang: string) => {
            //console.log("Language changed →", newLang);
            localStorage.setItem("selectedLang", newLang);
            setLang(newLang);
          });
        }, delay);

        // cleanup
        return () => clearTimeout(timer);
      }
    };

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  

  useEffect(() => {
    const interval = setInterval(() => {
      const storedLanguage = localStorage.getItem("selectedLang");
      if (storedLanguage) {
        setLang(storedLanguage);
        clearInterval(interval);
      }
    }, 1000); // check every second
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (!lang || !isWeglotReady || !window.Weglot) return;
    try {
      const userLang = getLanguage();
      const current = window.Weglot.getCurrentLang();

      if (current !== userLang) {
        //console.log("Switching Weglot to user language:", userLang);
        window.Weglot.switchTo(userLang);
        localStorage.setItem("selectedLang", userLang);
      }

    } catch (err) {
      console.error("Weglot switch error:", err);
    }
  }, [lang, isWeglotReady]);


  /**
   * On banner close
   */
  const onCloseBanner = () => {
    localStorage.setItem("bannerVisited", "true");
    setBannerVisited(true);
  }


  /**
   * Check banner should display or not
   */
  const shouldBannerDisplay = useMemo(() => {
    if (bannerVisited) return false;
    if (!bannerListData.length) return false;

    const banner = bannerListData[0];

    if (!banner.visibility) return false;
    if (!banner.startDate || !banner.endDate) return false;

    const now = new Date();
    
    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    ).getTime();

     const start = new Date(banner.startDate).setHours(0, 0, 0, 0);
     const end = new Date(banner.endDate).setHours(23, 59, 59, 999);

     return today >= start && today < end;
  }, [bannerListData, bannerVisited]);

  return (
    <>
      <AppInsightsErrorBoundary onError={() => <h1>{DEFAULT_ERROR_MSG}</h1>} appInsights={reactPlugin}>
        <>
          <LoaderProvider>
            {
              shouldBannerDisplay &&
              <Banner
                messages={[bannerListData[0].message]}
                autoHideTime={0}
                onClose={() => onCloseBanner()}
              />
            }
            <AppRouter />
          </LoaderProvider>
          <ToastContainer position="top-right" autoClose={3000} />
        </>
      </AppInsightsErrorBoundary>
    </>
  );
}

export default App;
